import React, { useState } from 'react'
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Link as MuiLink
} from '@mui/material'
import { router, usePage } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'
import StatusBadge from '../../../../components/ui/StatusBadge'

Show.layout = (page) => <AppShell>{page}</AppShell>

const STEP_LABELS = {
  sachkontrolle: 'Sachkontrolle',
  freigabe: 'Freigabe',
  rejection: 'Ablehnung',
}

export default function Show({ property, invoice, can_approve = false }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const invoicePath = `${basePath}/invoices/${invoice.id}`

  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  const post = (path, data = {}) => {
    router.post(`${invoicePath}/${path}`, data)
  }

  const submit = () => post('submit')
  const startReview = () => post('start_review')
  const approve = () => post('approve')
  const deleteInvoice = () => {
    if (window.confirm('Entwurf wirklich löschen?')) {
      router.delete(invoicePath)
    }
  }
  const submitReject = () => {
    post('reject', { reason: rejectReason })
    setRejectOpen(false); setRejectReason('')
  }
  const submitCancel = () => {
    post('cancel', { reason: cancelReason })
    setCancelOpen(false); setCancelReason('')
  }

  const isDraft = invoice.status === 'draft'
  const isSubmitted = invoice.status === 'submitted'
  const isUnderReview = invoice.status === 'under_review'
  const isRejected = invoice.status === 'rejected'
  const canCancel = ['approved', 'payment_initiated', 'paid'].includes(invoice.status)

  return (
    <Box>
      <PageHeader
        title={`Rechnung ${invoice.invoice_number}`}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Rechnungen', href: `${basePath}/invoices` },
          { label: invoice.invoice_number },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            {isDraft && (
              <>
                <Button variant="contained" size="small" onClick={submit}
                  data-testid="submit-btn">
                  Einreichen
                </Button>
                <Button variant="outlined" size="small"
                  onClick={() => router.visit(`${invoicePath}/edit`)}
                  data-testid="edit-btn">
                  Bearbeiten
                </Button>
                <Button variant="outlined" size="small" color="error"
                  onClick={deleteInvoice} data-testid="delete-btn">
                  Löschen
                </Button>
              </>
            )}
            {isSubmitted && (
              <>
                <Button variant="contained" size="small" onClick={startReview}
                  data-testid="start-review-btn">
                  Prüfung starten
                </Button>
                <Button variant="outlined" size="small" color="error"
                  onClick={() => setRejectOpen(true)}
                  data-testid="reject-btn">
                  Ablehnen
                </Button>
              </>
            )}
            {isUnderReview && (
              <>
                <Button variant="contained" size="small" onClick={approve}
                  disabled={!can_approve}
                  title={!can_approve ? 'Vier-Augen-Prinzip: Einreicher darf nicht freigeben.' : undefined}
                  data-testid="approve-btn">
                  Freigeben
                </Button>
                <Button variant="outlined" size="small" color="error"
                  onClick={() => setRejectOpen(true)}
                  data-testid="reject-btn">
                  Ablehnen
                </Button>
              </>
            )}
            {isRejected && (
              <>
                <Button variant="contained" size="small" onClick={submit}
                  data-testid="submit-btn">
                  Erneut einreichen
                </Button>
                <Button variant="outlined" size="small"
                  onClick={() => router.visit(`${invoicePath}/edit`)}
                  data-testid="edit-btn">
                  Bearbeiten
                </Button>
              </>
            )}
            {canCancel && (
              <Button variant="outlined" size="small" color="error"
                onClick={() => setCancelOpen(true)}
                data-testid="cancel-btn">
                Stornieren
              </Button>
            )}
          </Stack>
        }
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Lieferant: {invoice.vendor_name || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Datum: {invoice.invoice_date}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fällig: {invoice.due_date}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Netto: <FormattedCurrency value={invoice.net_amount} align="left" />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            USt: <FormattedCurrency value={invoice.vat_amount} align="left" />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Brutto: <FormattedCurrency value={invoice.gross_amount} align="left" />
          </Typography>
          <StatusBadge type="invoice_status" value={invoice.status} />
        </Box>
        {invoice.description && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {invoice.description}
          </Typography>
        )}
        {invoice.rejection_reason && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Ablehnungsgrund: {invoice.rejection_reason}
          </Typography>
        )}
        {invoice.cancellation_reason && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Stornogrund: {invoice.cancellation_reason}
          </Typography>
        )}
      </Paper>

      {/* Zuordnungen */}
      {invoice.units && invoice.units.length > 0 && (
        <Section title="Zuordnung zu Einheiten">
          <TableContainer component={Paper} elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Anteil %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.units.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.unit_number}</TableCell>
                    <TableCell align="right">{u.allocation_percentage || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      )}

      {/* Freigabeverlauf */}
      <Section title="Freigabeverlauf">
        {(!invoice.approval_steps || invoice.approval_steps.length === 0) && (
          <Typography color="text.secondary" sx={{ px: 1 }}>Noch keine Schritte.</Typography>
        )}
        {invoice.approval_steps && invoice.approval_steps.length > 0 && (
          <TableContainer component={Paper} elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Schritt</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ausgeführt</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Von</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Kommentar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.approval_steps.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{STEP_LABELS[s.step_type] || s.step_type}</TableCell>
                    <TableCell>{new Date(s.performed_at).toLocaleString('de-DE')}</TableCell>
                    <TableCell>{s.performed_by?.name || '—'}</TableCell>
                    <TableCell>{s.comment || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {/* Dokumente */}
      <Section title="Dokumente">
        {(!invoice.documents || invoice.documents.length === 0) && (
          <Typography color="text.secondary" sx={{ px: 1 }}>Keine Dokumente angehängt.</Typography>
        )}
        {invoice.documents && invoice.documents.map((d) => (
          <Box key={d.id} sx={{ mb: 1 }}>
            {d.url ? (
              <MuiLink href={d.url} target="_blank" rel="noopener">
                {d.title} ({d.filename})
              </MuiLink>
            ) : (
              <Typography variant="body2">{d.title}</Typography>
            )}
          </Box>
        ))}
      </Section>

      {/* Buchungen */}
      <Section title="Buchungen">
        {(!invoice.journal_entries || invoice.journal_entries.length === 0) && (
          <Typography color="text.secondary" sx={{ px: 1 }}>Noch nicht gebucht.</Typography>
        )}
        {invoice.journal_entries && invoice.journal_entries.length > 0 && (
          <TableContainer component={Paper} elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nr.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Soll</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Haben</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.journal_entries.map((je) => (
                  <TableRow key={je.id}>
                    <TableCell>{je.entry_number || je.id}</TableCell>
                    <TableCell>{je.entry_date}</TableCell>
                    <TableCell>{je.description}</TableCell>
                    <TableCell align="right"><FormattedCurrency value={je.total_debit} /></TableCell>
                    <TableCell align="right"><FormattedCurrency value={je.total_credit} /></TableCell>
                    <TableCell>
                      <StatusBadge type="journal_entry_status" value={je.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {/* Dialogs */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rechnung ablehnen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" fullWidth multiline minRows={2}
            label="Ablehnungsgrund" value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'reject-reason' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={submitReject}
            disabled={!rejectReason.trim()} data-testid="reject-submit">
            Ablehnen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rechnung stornieren</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" fullWidth multiline minRows={2}
            label="Stornogrund" value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'cancel-reason' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={submitCancel}
            disabled={!cancelReason.trim()} data-testid="cancel-submit">
            Stornieren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography>
      {children}
    </Box>
  )
}
